import type { HoldingsDataProps, HoldingsGridRowData } from '..';
import { useHoldingsColumns } from './useHoldingsColumns';
import { useHoldings } from './useHoldings';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { assert, assertExhaustive, type CustomBlockTypeEnum, isHoldingsBreakdownBlock } from 'venn-utils';
import { useRecoilValue } from 'recoil';
import { blockSettings, showBlockChangesColumn } from 'venn-state';
import { getChangesColumnDef } from './gridStyling';
import { holdingsColumnValueGetter } from './holdingsUtils';

const CREDIT_ORDERING = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'Below B', 'Not Rated', 'Unknown'];
const MATURITY_ORDERING = ['0-1 years', '1-3 years', '3-5 years', '5-7 years', '7-10 years', '10+ years', 'Unknown'];
const DURATION_ORDERING = ['0-1 years', '1-3 years', '3-5 years', '5-10 years', '10+ years', 'Unknown'];

const sortOnFixedOrdering = (rowData: HoldingsGridRowData[], ordering: string[]) => {
  return rowData.sort((a, b) => {
    return ordering.indexOf(a.label) - ordering.indexOf(b.label);
  });
};

const sortRowData = (blockType: CustomBlockTypeEnum, rowData: HoldingsGridRowData[]) => {
  assert(isHoldingsBreakdownBlock(blockType));
  switch (blockType) {
    case 'ASSET_EXPOSURE':
    case 'SECTOR_EXPOSURE':
    case 'GEOGRAPHY_EXPOSURE':
      return rowData;
    case 'DURATION_BREAKDOWN':
      return sortOnFixedOrdering(rowData, DURATION_ORDERING);
    case 'MATURITY_BREAKDOWN':
      return sortOnFixedOrdering(rowData, MATURITY_ORDERING);
    case 'CREDIT_BREAKDOWN':
      return sortOnFixedOrdering(rowData, CREDIT_ORDERING);
    default: {
      return assertExhaustive(blockType);
    }
  }
};

const useHoldingsDataGrid = ({ selectedRefId }: Pick<HoldingsDataProps, 'selectedRefId'>) => {
  const { data, categories } = useHoldings(selectedRefId);
  const settings = useRecoilValue(blockSettings(selectedRefId));
  const blockType = settings.customBlockType;
  const showChangesColumn = useRecoilValue(showBlockChangesColumn(selectedRefId));

  const rowData = useMemo((): HoldingsGridRowData[] => {
    const subjectToAllocationMap = data?.breakdowns?.map((breakdown) =>
      Object.fromEntries(breakdown.holdingsContributions.map((bd) => [bd.id.id, bd.allocation])),
    );

    const rows = categories
      .map((category) => {
        const subjectToAllocation = subjectToAllocationMap?.map((map) => map[category.id.id] ?? 0) ?? [];
        const changes =
          subjectToAllocation?.length === 2 && !isNil(subjectToAllocation[1]) && !isNil(subjectToAllocation[0])
            ? subjectToAllocation[0] - subjectToAllocation[1]
            : 0;

        return {
          id: category.id,
          key: category.id.id,
          label: category.categoryName,
          type: 'PERCENTAGE' as const,
          value: subjectToAllocation,
          changes,
        };
      })
      .filter((row) => row.value?.some((alloc) => alloc));
    return sortRowData(blockType, rows);
  }, [categories, data?.breakdowns, blockType]);

  const originalColumnDefs = useHoldingsColumns({
    selectedRefId,
    valueGetter: holdingsColumnValueGetter,
  });

  const columnDefs = useMemo(() => {
    if (!showChangesColumn) return originalColumnDefs;

    return [...originalColumnDefs, getChangesColumnDef()];
  }, [originalColumnDefs, showChangesColumn]);

  return {
    rowData,
    columnDefs,
  };
};

export default useHoldingsDataGrid;
